import { useEffect, useRef, useState } from 'react'
import { Mic, MicOff, PhoneOff, Video, VideoOff, Volume2, VolumeX } from 'lucide-react'
import { Room, RoomEvent, Track } from 'livekit-client'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { useWalletBalance } from '@/features/reels/hooks/useWalletBalance'
import { formatCallCopy } from './CallButton'

export interface ActiveCall {
  callId: string
  token: string
  wsUrl: string
  pricePerMinute: number
  peerName: string
  /** R32: video intent from the session. Drives camera publish + remote video render. */
  withVideo: boolean
}

interface VoiceCallScreenProps {
  call: ActiveCall
  onEnd: (callId: string) => void
  /** Test seam: defaults to a real audio-only `livekit-client` Room. */
  roomFactory?: () => Room
}

/** Give up joining after this long: a hung mic prompt or handshake must surface, never spin forever. */
export const CONNECT_TIMEOUT_MS = 20000

/** mm:ss elapsed. */
export function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * Whole minutes billed so far. The backend ticks every 60s and debits
 * `ceil(elapsed) - billed`, so the running cost mirrors it: a partial
 * minute already counts.
 */
export function billedMinutes(elapsedSeconds: number): number {
  return elapsedSeconds <= 0 ? 0 : Math.ceil(elapsedSeconds / 60)
}

/**
 * Voice-only call screen (video comes later): joins the `call-{id}` room
 * with the publisher token, publishes the mic, renders the remote audio
 * element, and shows a live timer with the running cost. Remote video
 * tracks are never attached — audio only.
 */
export function VoiceCallScreen({ call, onEnd, roomFactory }: VoiceCallScreenProps) {
  const { strings } = useLanguage()
  const copy = strings.calls
  const { balance } = useWalletBalance()
  const [status, setStatus] = useState<'connecting' | 'active' | 'error'>('connecting')
  const [muted, setMuted] = useState(false)
  const [speakerOn, setSpeakerOn] = useState(true)
  const [cameraOff, setCameraOff] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [room, setRoom] = useState<Room | null>(null)
  const roomRef = useRef<Room | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const remoteVideoRef = useRef<HTMLVideoElement>(null)
  const localVideoRef = useRef<HTMLVideoElement>(null)
  const endedRef = useRef(false)
  const callIdRef = useRef(call.callId)
  callIdRef.current = call.callId

  const finish = (id: string) => {
    if (endedRef.current) return
    endedRef.current = true
    onEnd(id)
  }

  // Join once: audio-only room, mic published on entry (mirrors the
  // broadcast screen enabling mic right after joining).
  //
  // A hung step (mic permission prompt never answered, stalled room
  // handshake) neither resolves nor rejects, so without a guard the UI
  // sits on "Connecting…" forever. The timeout below fails it into the
  // error state. Billing starts server-side on Accept, before the
  // client ever reaches this room — so a join that fails or times out
  // must also call `finish()` (→ onEnd → the server EndCall RPC)
  // immediately, instead of leaving the session active and waiting for
  // the user to notice and tap "End call", or for the wallet to run
  // dry. Caught live: 5 minutes billed against a frozen 00:00 screen,
  // only stopped by the insufficient-balance auto-cutoff.
  useEffect(() => {
    let cancelled = false
    let settled = false
    let liveRoom: Room | null = null
    let timer: ReturnType<typeof setInterval> | null = null
    const timeout = setTimeout(() => {
      if (!settled && !cancelled) {
        settled = true
        setStatus('error')
        finish(callIdRef.current)
        if (liveRoom) void liveRoom.disconnect().catch(() => {})
      }
    }, CONNECT_TIMEOUT_MS)
    void (async () => {
      try {
        liveRoom = roomFactory ? roomFactory() : new Room({ adaptiveStream: true, dynacast: true })
        roomRef.current = liveRoom
        liveRoom.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Audio) {
            const el = audioRef.current
            if (el) track.attach(el)
          } else if (track.kind === Track.Kind.Video) {
            // Remote camera. Audio-only calls never publish video, so this
            // only fires on video calls (the local preview attaches below).
            const el = remoteVideoRef.current
            if (el) track.attach(el)
          }
        })
        liveRoom.on(RoomEvent.Disconnected, () => finish(callIdRef.current))
        await liveRoom.connect(call.wsUrl, call.token)
        if (cancelled) {
          await liveRoom.disconnect()
          return
        }
        await liveRoom.localParticipant.setMicrophoneEnabled(true)
        if (cancelled) {
          await liveRoom.disconnect()
          return
        }
        // Video intent comes from the session (R32): publish the camera
        // too. A denied/failed camera throws into the same error path as
        // a mic failure — error state + immediate server-side end, so no
        // silent billing against a broken call.
        if (call.withVideo) {
          await liveRoom.localParticipant.setCameraEnabled(true)
          if (cancelled) {
            await liveRoom.disconnect()
            return
          }
        }
        settled = true
        clearTimeout(timeout)
        setRoom(liveRoom)
        setStatus('active')
        timer = setInterval(() => setElapsed((e) => e + 1), 1000)
      } catch {
        settled = true
        clearTimeout(timeout)
        if (!cancelled) {
          setStatus('error')
          finish(callIdRef.current)
        }
        if (liveRoom) await liveRoom.disconnect().catch(() => {})
      }
    })()
    return () => {
      cancelled = true
      clearTimeout(timeout)
      if (timer) clearInterval(timer)
      void liveRoom?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.callId])

  // Attach the local camera track to the preview element (mirrors the
  // broadcast screen). Runs only on video calls; `room` is set on join.
  useEffect(() => {
    const video = localVideoRef.current
    const publication = room?.localParticipant.getTrackPublication(Track.Source.Camera)
    const track = publication?.videoTrack
    if (video && track) {
      track.attach(video)
      return () => {
        track.detach()
      }
    }
    return undefined
  }, [room, cameraOff, status])

  const toggleCamera = async () => {
    const liveRoom = roomRef.current
    if (!liveRoom) return
    try {
      const next = !cameraOff
      await liveRoom.localParticipant.setCameraEnabled(!next)
      setCameraOff(next)
    } catch {
      // Ignore toggle failures like mobile.
    }
  }

  const toggleMute = async () => {
    const room = roomRef.current
    if (!room) return
    try {
      const next = !muted
      await room.localParticipant.setMicrophoneEnabled(!next)
      setMuted(next)
    } catch {
      // Ignore toggle failures like mobile.
    }
  }

  // Best-effort web speaker toggle: routes the remote audio element
  // between full volume and silent (mobile switches earpiece/speaker).
  const toggleSpeaker = () => {
    const next = !speakerOn
    setSpeakerOn(next)
    const el = audioRef.current
    if (el) el.volume = next ? 1 : 0
  }

  const cost = billedMinutes(elapsed) * call.pricePerMinute

  const showVideo = call.withVideo && status === 'active'

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-lm-surface" role="dialog" aria-label={call.peerName}>
      {showVideo && (
        <div className="relative h-[38%] w-full bg-black">
          <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" data-testid="remote-video" />
          <video
            ref={localVideoRef}
            muted
            playsInline
            autoPlay
            className="absolute bottom-2 right-2 h-24 w-16 rounded-lg border border-white/40 bg-black object-cover"
            data-testid="local-preview"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        {!showVideo && (
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-lm-primary-container text-headline-md font-bold text-lm-on-primary-container">
            {call.peerName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <h2 className="mt-4 text-headline-sm font-bold text-lm-on-surface">{call.peerName}</h2>
        {status === 'connecting' && <p className="mt-1 text-body-md text-lm-on-surface-variant">{copy.connecting}</p>}
        {status === 'error' && (
          <p role="alert" className="mt-1 text-body-md text-lm-error">
            {copy.joinFailed}
          </p>
        )}
        <p className="mt-2 text-title-lg font-semibold tabular-nums text-lm-on-surface">{formatElapsed(elapsed)}</p>
        <p className="mt-1 text-body-md text-lm-on-surface-variant">
          {`${formatCallCopy(copy.cost, cost)} · ${formatCallCopy(copy.perMin, call.pricePerMinute)}`}
        </p>
        <p className="mt-1 text-body-sm text-lm-on-surface-variant">{formatCallCopy(copy.balance, balance)}</p>
        {/* Remote voice. Never rendered for video: only audio tracks attach. */}
        <audio ref={audioRef} autoPlay playsInline data-testid="remote-audio" />
      </div>
      <div className="flex items-center justify-center gap-4 px-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        {showVideo && (
          <button
            type="button"
            aria-label={cameraOff ? copy.cameraOn : copy.cameraOff}
            title={cameraOff ? copy.cameraOn : copy.cameraOff}
            onClick={() => void toggleCamera()}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-lm-surface-container-high text-lm-on-surface"
          >
            {cameraOff ? <VideoOff size={22} /> : <Video size={22} />}
          </button>
        )}
        <button
          type="button"
          aria-label={muted ? copy.unmute : copy.mute}
          title={muted ? copy.unmute : copy.mute}
          onClick={() => void toggleMute()}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-lm-surface-container-high text-lm-on-surface"
        >
          {muted ? <MicOff size={22} /> : <Mic size={22} />}
        </button>
        <button
          type="button"
          aria-label={copy.endCall}
          title={copy.endCall}
          onClick={() => finish(call.callId)}
          className="flex h-16 w-16 items-center justify-center rounded-full bg-lm-error text-lm-on-error"
        >
          <PhoneOff size={26} />
        </button>
        <button
          type="button"
          aria-label={copy.speaker}
          aria-pressed={speakerOn}
          title={copy.speaker}
          onClick={toggleSpeaker}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-lm-surface-container-high text-lm-on-surface"
        >
          {speakerOn ? <Volume2 size={22} /> : <VolumeX size={22} />}
        </button>
      </div>
    </div>
  )
}
